import { createFileRoute, Outlet, redirect, useRouter } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiError } from '@/api/client'
import { meQuery } from '@/api/queries'
import { getUser, logout } from '@/auth/oidc'
import { AppShell } from '@/components/AppShell'
import { EmptyState } from '@/components/page'
import { Button } from '@/components/ui/button'
import { applyUserLocale } from '@/i18n'

export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ context, location }) => {
    if (!(await getUser())) {
      throw redirect({ to: '/login', search: { returnTo: location.href } })
    }
    const me = await context.queryClient.ensureQueryData(meQuery)
    return { me }
  },
  component: AppLayout,
  errorComponent: MeError,
})

function AppLayout() {
  const { me } = Route.useRouteContext()
  useEffect(() => applyUserLocale(me.locale), [me.locale])
  return (
    <AppShell me={me}>
      <Outlet />
    </AppShell>
  )
}

function MeError({ error }: { error: unknown }) {
  const { t } = useTranslation()
  const router = useRouter()
  const forbidden = error instanceof ApiError && (error.status === 403 || error.status === 404)
  return (
    <main id="main-content" className="mx-auto max-w-xl p-6">
      <EmptyState
        title={t('meError.title')}
        body={forbidden ? t('meError.forbidden') : t('meError.body')}
      >
        <div className="flex gap-3">
          {!forbidden && (
            <Button onClick={() => void router.invalidate()}>{t('common.retry')}</Button>
          )}
          <Button variant="outline" onClick={() => void logout()}>
            {t('header.logout')}
          </Button>
        </div>
      </EmptyState>
    </main>
  )
}
